import { notifyError } from "@/lib/notify-error";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { toast } from "sonner";

import type { GroupMember } from "./merge-layout";

type CreateGroupArgs = {
  name: string;
  members: GroupMember[];
  directions?: string;
};
type UpdateGroupArgs = {
  name?: string;
  directions?: string;
};

/** Table selection and merge mutations for a SpecPilot job; each failure is reported, then rethrown. */
export function useRowGroupActions(
  submittalId: Id<"specPilotSubmittals">,
  onAction?: () => void | Promise<void>
) {
  const updateSelection = useMutation(api.specPilot.extractedTables.updateSelection);
  const createRowGroup = useMutation(api.specPilot.rowGroups.create);
  const updateRowGroup = useMutation(api.specPilot.rowGroups.update);
  const removeRowGroup = useMutation(api.specPilot.rowGroups.remove);

  async function handleSelectionChange(
    tableId: Id<"specPilotExtractedTables">,
    selected: boolean
  ) {
    try {
      await updateSelection({ tableId, selected });
      void onAction?.();
    } catch (error) {
      notifyError(error, "Failed to update selection");
      console.error(error);
    }
  }

  async function handleCreateGroup(args: CreateGroupArgs) {
    try {
      await createRowGroup({
        submittalId,
        name: args.name,
        members: args.members.map((member) => ({
          tableId: member.tableId as Id<"specPilotExtractedTables">,
          rowIndex: member.rowIndex,
        })),
        ...(args.directions !== undefined ? { directions: args.directions } : {}),
      });
      void onAction?.();
      toast.success("Rows merged");
    } catch (error) {
      notifyError(error, "Failed to merge rows");
      console.error(error);
      throw error;
    }
  }

  async function handleUpdateGroup(groupId: string, args: UpdateGroupArgs) {
    try {
      await updateRowGroup({
        groupId: groupId as Id<"specPilotRowGroups">,
        ...(args.name !== undefined ? { name: args.name } : {}),
        ...(args.directions !== undefined ? { directions: args.directions } : {}),
      });
      void onAction?.();
      toast.success("Merged rows updated");
    } catch (error) {
      notifyError(error, "Failed to update merged rows");
      console.error(error);
      throw error;
    }
  }

  async function handleDeleteGroup(groupId: string) {
    try {
      await removeRowGroup({ groupId: groupId as Id<"specPilotRowGroups"> });
      void onAction?.();
      toast.success("Merged rows deleted");
    } catch (error) {
      notifyError(error, "Failed to delete merged rows");
      console.error(error);
      throw error;
    }
  }

  return {
    handleSelectionChange,
    handleCreateGroup,
    handleUpdateGroup,
    handleDeleteGroup,
  };
}
